import React, { FC, useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { saveQualificationDetailApi } from "./JoinNowForm.api";
import { toastrError, toastrSuccess } from "lib/toastr";
import { useAuth } from "context/auth-context";
import { getCountry } from "./getdata";
import { FiUser } from "react-icons/fi";

export interface ICard {
  closepopup: () => void;
  email: string;
}

export const JoinQualificationpopup: FC<ICard> = ({ closepopup, email }) => {
  const { auth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [countryname, setCountryName] = useState('');
  const [formData, setFormData] = useState({
    dob: "",
    gender: "",
    mobile: "",
    zipcode: "",
  });

  useEffect(() => {getCountry(setCountryName);}, []);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.dob) {
      newErrors.dob = "Date of Birth is required";
    } else {
      const today = new Date();
      const dobDate = new Date(formData.dob);
      let age = today.getFullYear() - dobDate.getFullYear();
      const monthDiff = today.getMonth() - dobDate.getMonth();
      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < dobDate.getDate())
      ) {
        age--;
      }
      if (age < 1) {
        newErrors.dob = "You must be at least 1 year old";
      }
    }

    if (!formData.gender) {
      newErrors.gender = "Gender is required";
    }

    if (!formData.zipcode) {
      newErrors.zipcode = "Postal/Zip Code is required";
    } else if (formData.zipcode.trim() === "") {
      newErrors.zipcode = "Postal/Zip Code cannot be only spaces";
    } else if (/^\s/.test(formData.zipcode)) {
      newErrors.zipcode = "Postal/Zip Code cannot start with a space";
    }

    // if (!formData.education) {
    //   newErrors.education = "Education is required";
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const saveuserdetails = useMutation(
    () => saveQualificationDetailApi(formData, email, auth.panelistId, countryname),
    {
      onSuccess: (response) => {
        if (response.success) {
          toastrSuccess(response.message || "User details saved successfully");
          setLoading(false);
          closepopup();
        } else {
          toastrError(response.message || "Saving user details failed");
          setLoading(false);
        }
      },
      onError: () => {
        toastrError("Failed to save user details");
        setLoading(false);
      },
    }
  );


  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      setLoading(true);
      saveuserdetails.mutate();
    }
  };

  return (
    <div className="zs-backdrop" role="dialog" aria-modal="true" aria-labelledby="join-qualification-title">
      <div className="zs-modal max-w-[460px]">
        {/* Header */}
        <div className="border-b border-line px-6 pt-7 pb-5 sm:px-8">
          <span className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary-soft text-primary-text">
            <FiUser className="h-5 w-5" aria-hidden="true" />
          </span>
          <h2 id="join-qualification-title" className="font-display text-xl font-bold tracking-tight text-ink sm:text-2xl">
            Welcome!
          </h2>
          <p className="mt-1 text-sm text-ink-2">
            Let’s personalize your experience
          </p>
          <p className="mt-0.5 text-sm text-ink-3">Answer a few quick questions</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5 px-6 py-6 sm:px-8">
          {/* Age */}
          {/* Date of Birth */}
          <div>
            <label className="zs-label zs-label-required">
              What is your date of birth?
            </label>
            <input
              type="date"
              value={formData.dob}
              onChange={(e) => {
                setFormData({ ...formData, dob: e.target.value });
                if (errors.dob) {
                  setErrors({ ...errors, dob: "" });
                }
              }}
              aria-invalid={errors.dob ? true : undefined}
              className="zs-input"
            />
            {errors.dob && (
              <p className="zs-error">{errors.dob}</p>
            )}
          </div>


          {/* Gender */}
          <div>
            <label className="zs-label zs-label-required">
              What is your gender?
            </label>
            <div className="grid grid-cols-3 gap-2">
              {["Male", "Female", "Other"].map((option) => (
                <label
                  key={option}
                  className={`relative flex h-[42px] cursor-pointer items-center justify-center gap-2 rounded border text-sm font-medium transition-colors ${
                    formData.gender === option
                      ? "border-primary bg-primary-soft text-primary-text"
                      : "border-line-strong bg-surface text-ink-2 hover:bg-surface-hover"
                  }`}
                >
                  <input
                    type="radio"
                    name="gender"
                    value={option}
                    checked={formData.gender === option}
                    onChange={(e) => {
                      setFormData({ ...formData, gender: e.target.value });
                      if (errors.gender) {
                        setErrors({ ...errors, gender: "" });
                      }
                    }}
                    className="sr-only"
                  />
                  <span
                    className={`flex h-4 w-4 items-center justify-center rounded-full border-2
                    ${formData.gender === option
                        ? "border-primary"
                        : "border-line-strong"
                      }`}
                  >
                    {formData.gender === option && (
                      <span className="h-1.5 w-1.5 rounded-full bg-primary"></span>
                    )}
                  </span>
                  <span className="text-sm">{option}</span>
                </label>
              ))}
            </div>
            {errors.gender && (
              <p className="zs-error">{errors.gender}</p>
            )}
          </div>

          {/* Postal/Zip Code */}
          <div>
            <label className="zs-label zs-label-required">
              What&apos;s your postal / ZIP code?
            </label>
            <input
              type="text"
              value={formData.zipcode}
              onChange={(e) => {
                setFormData({ ...formData, zipcode: e.target.value });
                if (errors.zipcode) {
                  setErrors({ ...errors, zipcode: "" });
                }
              }}
              aria-invalid={errors.zipcode ? true : undefined}
              className="zs-input"
              placeholder="e.g. 123456"
            />
            {errors.zipcode && (
              <p className="zs-error">{errors.zipcode}</p>
            )}
          </div>

          {/* Mobile Number */}
          <div>
            <label className="zs-label">
              What is your mobile number?
            </label>
            <input
              type="tel"
              value={formData.mobile}
              onChange={(e) =>
                setFormData({ ...formData, mobile: e.target.value })
              }
              className="zs-input"
              placeholder="mobile number"
            />
            {errors.mobile && (
              <p className="zs-error">{errors.mobile}</p>
            )}
          </div>

          {/* Submit */}
          <div className="pt-2">
            <button
              type="submit"
              className="zs-btn zs-btn-primary zs-btn-lg zs-btn-block"
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
